import React, { Fragment, useEffect, useRef, useState } from 'react';
import styles from './styles.scss';
import classnames from 'classnames';
import moment from 'moment-timezone';
import { NoteAction, Capture } from '../../types/api';
import { ConfigV1 } from '../../types/config';
import { ConfigStatus, ConfigType } from '../../types/sensor-configuration';
import ReactQuill from 'react-quill';
import AddUpdateNoteForm from '../AddUpdateNoteForm';

import CaptureViewer from '../CaptureViewer';
import { AppBar, AppBarSection, Icons, Button } from '@densityco/ui';
import HorizontalForm from '../HorizontalForm';
import Tooltip from '../Tooltip';
import DataGrid, { DataGridProps } from '../DataGrid';

// import sanitizeHtml from 'sanitize-html';
import DOMPurify from 'dompurify';
import { formatISOTimestamp } from '../../utils/time';

type IconType = typeof Icons['Algo'];

type NoteTypeValue = {
  name: React.ReactNode;
  icon: IconType | null;
  collapseButtonHidden?: boolean;
};

export const NOTE_TYPES: { [key: string]: NoteTypeValue } = {
  USER_GENERAL: {
    name: 'Note',
    icon: Icons.Study,
  },
  USER_CONFIGURATION: {
    name: 'Configuration Changed',
    icon: Icons.Algo,
  },
  USER_SENSOR_ADDED_TO_MPI: {
    name: 'Sensor Added To MPI',
    icon: Icons.Integrations1,
  },
  USER_SENSOR_REMOVED_FROM_MPI: {
    name: 'Sensor Removed From MPI',
    icon: Icons.Integrations1,
  },
  USER_DATA_CAPTURES_TRIGGERED: {
    name: 'Data Captures Triggered',
    icon: Icons.ClockTimer,
  },
  USER_DATA_CAPTURES_SCHEDULED: {
    name: 'Data Captures Scheduled',
    icon: Icons.ClockTimer,
  },
  USER_DATA_CAPTURES_STOPPED: {
    name: 'Data Captures Stopped',
    icon: Icons.ClockReset,
  },
  USER_PERFORMED_NOTE_ACTION: {
    name: 'User Performed Action',
    icon: Icons.Person,
  },
  USER_RECALIBRATING_SENSOR: {
    name: 'Recalibrating Sensor',
    icon: Icons.LinkBroken,
  },
  SYSTEM_SENSOR_HEIGHT_MEASUREMENT: {
    name: 'Sensor Height Measurement',
    icon: Icons.Ruler,
  },
  SYSTEM_EVENT: {
    name: 'System Event',
    icon: Icons.Control1,
  },
  SYSTEM_SENSOR_AUTO_OS_UPDATE: {
    name: 'Operating System Updated',
    icon: Icons.Refresh,
  },
  SYSTEM_SENSOR_STATUS_CHANGE: {
    name: 'Sensor Status',
    icon: Icons.Power,
  },
  SYSTEM_CURRENT_STATE_CHANGE: {
    name: 'State Changed',
    icon: Icons.Refresh,
  },
  SYSTEM_AUTOMATIC_DATA_CAPTURES_TRIGGERED_SUCCESS: {
    name: 'Automatic Data Captures Triggered',
    icon: Icons.ClockTimer,
  },
  SYSTEM_ASSIGNED_TEAM_CHANGED: {
    name: 'Assigned Team Changed',
    icon: Icons.Team,
  },
  SYSTEM_SENSOR_OS_UPDATE_REQUIRED: {
    name: 'Update to latest OS required',
    icon: Icons.IngressUp,
  },
  SYSTEM_SENSOR_UNABLE_TO_GENERATE_MORE_ANNOTATED_EVENTS: {
    name: 'Unable to generate more annotated events',
    icon: Icons.Alert,
  },
  SYSTEM_SENSOR_EVENT_LOOP_OCCURRED_TOO_MANY_TIMES: {
    name: '175 annotated events were unable to be collected in 60 days',
    icon: Icons.Info,
  },
  SYSTEM_SENSOR_NOT_ONLINE: {
    name: 'Sensor Offline',
    icon: Icons.Power,
  },
  SYSTEM_SENSOR_DATA_CAPTURE_COMMAND_FAILED: {
    name: 'Sensor Start Capture Command Failed',
    icon: Icons.Danger,
  },
  SYSTEM_SENSOR_SLA_VALIDATED: {
    name: 'Sensor SLA Validated',
    icon: Icons.Check,
  },
  SYSTEM_SENSOR_SLA_FAILED: {
    name: 'Sensor SLA Failed',
    icon: Icons.Close,
  },
  SYSTEM_MPI_MANUAL_PROCESSING_REQUIRED: {
    name: 'Sensor is part of a MPI',
    icon: Icons.Integrations1,
  },

  SYSTEM_ACCURACY_LOOP_OCCURRED_TOO_MANY_TIMES: {
    name: 'Sensor "accuracy" loop occurred too many times',
    icon: Icons.Refresh,
  },
  SYSTEM_SENSOR_BAD_DATA_QUALITY: {
    name: 'Protective film was not removed',
    icon: Icons.Danger,
  },
  SYSTEM_SENSOR_HEIGHT_OUT_OF_SPECIFICATION: {
    name: 'Sensor height is out of specification',
    icon: Icons.Danger,
  },
  SYSTEM_SENSOR_READY_FOR_MANUAL_CONFIGURATION: {
    name: 'Ready to Be Configured',
    icon: Icons.Person,
  },
  SYSTEM_SENSOR_IS_NOT_ENTRY: {
    name: 'Sensor is an OA sensor - this is not yet supported',
    icon: Icons.DeviceSide,
  },
  SYSTEM_SENSOR_BLANK_DATA_CAPTURE_FAILED: {
    name: 'Unable to retrieve initial blank capture from sensor',
    icon: Icons.Danger,
  },
  SYSTEM_SENSOR_CHECK_SENSOR_HEIGHT_FAILED: {
    name: 'Unable to retrieve sensor height from sensor',
    icon: Icons.Danger,
  },
  SYSTEM_SENSOR_MEASURE_HEIGHT_FAILED: {
    name: 'Unable to retrieve sensor height from sensor',
    icon: Icons.Danger,
  },
  SYSTEM_SENSOR_CHECK_DATA_QUALITY_FAILED: {
    name: 'Unable to compute sensor data quality',
    icon: Icons.Danger,
  },
  SYSTEM_SENSOR_NEEDS_CONFIGURATION_VALIDATION: {
    name: 'Configuration needs validation',
    icon: Icons.Person,
  },

  // This is purely for sensei only and used so the initial "new note" card can be rendered.
  // The server never process notes of this type.
  CREATION_FORM: {
    name: 'New Note',
    icon: Icons.Plus,
    collapseButtonHidden: true,
  },
};

type NoteProps = {
  type: keyof typeof NOTE_TYPES;
  timestamp?: string;
  body: string | React.ReactNode;
  status?: 'default' | 'success' | 'danger' | 'warning';
  actions?: Array<NoteAction>,
  isCollapsedByDefault?: boolean,
  collapsedTitleContext?: React.ReactNode,

  from?: {
    email: string;
    full_name: string;
    picture: string | null;
  };
  attachments?: Array<React.ReactNode>;

  editing?: boolean;
  onEditNote?: () => void;
  onDeleteNote?: () => void;
  onCancelEditNote?: () => void;
  onSubmitEditNote?: () => void;
  footerDisabled?: boolean;
  submitDisabled?: boolean;
  onChangeBody?: (html: string) => void;
  onClickAction?: (action: NoteAction) => void;
};

export const NoteSanitizedHTMLRender: React.FunctionComponent<{
  body: React.ReactNode;
}> = ({ body }) => {
  if (body === null) {
    return null;
  }
  if (body === '') {
    return (
      <div className={classnames(styles.noteCardBody, styles.empty)}>
        Nothing to preview
      </div>
    );
  }

  const renderableBody =
    typeof body === 'string' ? (
      <div
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(body, {
            ADD_TAGS: ['h1', 'h2', 'u']
          })
        }}
      />
    ) : (
      // Children is jsx, so just render it
      body
    );

  return <div className={styles.noteCardBody}>{renderableBody}</div>;
};

const Note: React.FunctionComponent<NoteProps> = ({
  type,
  from = null,
  timestamp,
  attachments = [],
  body,
  actions = [],
  status = 'default',
  isCollapsedByDefault = false,
  collapsedTitleContext = null,

  editing,
  onEditNote,
  onDeleteNote,
  onCancelEditNote,
  onSubmitEditNote,
  footerDisabled,
  submitDisabled,
  onChangeBody,
  onClickAction,
}) => {
  const [noteTooltipVisible, setNoteTooltipVisible] = useState(false);
  const [preview, setPreview] = useState(false);
  const [collapsed, setCollapsed] = useState(isCollapsedByDefault);

  const { name, icon, collapseButtonHidden } = NOTE_TYPES[type] || {
    name: `Unknown note "${type}"`,
    icon: null,
    collapseButtonHidden: false,
  };
  const IconComponent = icon;

  const editorRef = useRef<ReactQuill>(null);
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  }, [editorRef, editing]);

  return (
    <div
      className={classnames(styles.note, {
        [styles.statusDanger]: status === 'danger',
        [styles.statusSuccess]: status === 'success',
        [styles.statusWarning]: status === 'warning',
        [styles.collapsed]: collapsed,
      })}
      data-test-id="note"
      data-test-note-type={type}
    >
      <div className={styles.noteArrow} />

      <div
        className={classnames(styles.bubble, {
          [styles.initialsBubble]: from,
          [styles.densityBubble]: !from,
        })}
      >
        <Tooltip
          target={
            from ? (
              <Fragment>
                {from.picture ? (
                  <img className={styles.bubbleAvatar} src={from.picture} />
                ) : null}
                {from.full_name
                  .split(' ')
                  .map(i => i[0])
                  .filter(i => i)
                  .map(i => i.toUpperCase())
                  .join('')}
              </Fragment>
            ) : (
              <Fragment>
                <div className={styles.densityBubbleBackground}>
                  <Icons.DensityMark width={12} height={12} color="#fff" />
                </div>
              </Fragment>
            )
          }
          contents={
            from ? `${from.full_name} - ${from.email}` : 'Density System Note'
          }
          placement="bottom"
        />
      </div>

      <div className={styles.timeline} />

      <div className={styles.noteCard}>
        <div className={styles.noteCardHeader}>
          <div className={styles.noteTitle}>
            {IconComponent ? (
              <IconComponent width={24} height={24} color="currentColor" />
            ) : null}
            <span>
              {name}
              {/* When the note is collapsed, render additional metadata in the title */}
              {collapsed && collapsedTitleContext ? <Fragment>
                {' '}- {collapsedTitleContext}
              </Fragment> : null}
            </span>
          </div>
          <HorizontalForm>
            {timestamp ? (
              <div className={styles.noteTimestamp}>{formatISOTimestamp(timestamp)}</div>
            ) : null}
            {!collapseButtonHidden ? (
              <Button
                onClick={e => setCollapsed(!collapsed)}
                variant={collapsed ? 'underline' : 'default'}
                leftIcon={collapsed ? <Icons.ChevronLeft /> : <Icons.ChevronDown />}
                width={40}
                height={40}
              />
            ) : null}
          </HorizontalForm>
        </div>

        {!collapsed ? (
          <Fragment>
            {editing ? (
              <AddUpdateNoteForm
                placeholder="Leave a comment"
                submitDisabled={submitDisabled}
                submitLabel="Save Note"
                onSubmit={onSubmitEditNote}
                value={body as string}
                onChangeValue={onChangeBody}
                ref={editorRef}
                buttons={
                  <Button variant="underline" onClick={onCancelEditNote}>
                    Cancel
                  </Button>
                }
                width="100%"
              />
            ) : null}
            {body && !editing ? <NoteSanitizedHTMLRender body={body} /> : null}

            {!footerDisabled ? (
              <div className={styles.noteCardFooter}>
                <button data-test-id="note-edit" onClick={onEditNote}>
                  Edit
                </button>
                {type === 'USER_GENERAL' ? (
                  <button data-test-id="note-delete" onClick={onDeleteNote}>
                    Delete
                  </button>
                ) : null}
              </div>
            ) : null}

            {/* Attachments are a means of adding content at the end of a note */}
            {attachments.reduce((acc, attachment) => {
              return (
                <Fragment>
                  {acc}
                  <div className={styles.noteAttachmentsWrapper}>
                    {attachment}
                  </div>
                </Fragment>
              );
            }, null)}

            {/* Actions render a button to allow a note to perform other operations */}
            {actions.length > 0 ? (
              <div className={styles.noteActionsWrapper}>
                <AppBar>
                  <AppBarSection />
                  <AppBarSection>
                    <HorizontalForm>
                      {actions.slice().reverse().map(action => (
                        <Button
                          key={action.label}
                          href={action.href}
                          variant={action.is_primary ? 'filled' : 'default'}
                          disabled={!action.enabled}
                          onClick={action.href ? undefined : () => {
                            if (onClickAction) {
                              onClickAction(action);
                            }
                          }}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </HorizontalForm>
                  </AppBarSection>
                </AppBar>
              </div>
            ) : null}
          </Fragment>
        ) : null}
      </div>
    </div>
  );
};
export default Note;

export const NoteConfigAttachment: React.FunctionComponent<{
  capture: Capture | null;
  config: ConfigV1;
  configType: ConfigType;
  configStatus: ConfigStatus;
}> = ({ capture, config, configType, configStatus }) => (
  <div className={styles.configAttachment}>
    <div className={styles.configAttachmentCapture}>
      {capture ? (
        <CaptureViewer
          readOnly
          autoPlay
          height={256}
          capture={capture}
          configState={{
            mutable_config: config,
            mutable_config_template: configType,
            config_status: configStatus,
          }}
        />
      ) : null}
    </div>
    <pre className={styles.configAttachmentJson}>
      {JSON.stringify(config, null, 2)}
    </pre>
  </div>
);

export const NoteTextAttachment: React.FunctionComponent = ({ children }) => (
  <div className={styles.textAttachment}>{children}</div>
);

export const NoteDataGridAttachment: React.FunctionComponent<DataGridProps> = (props) => (
  <div className={styles.dataGridAttachment}>
    <DataGrid {...props} />
  </div>
);
